import { createContext, useContext, useEffect, useMemo, useState } from "react";
import Loading from "../../components/Loading";
import {
  DataRoomPermission,
  FileType,
  useDataRoomFastQuery,
  useDataRoomFolderBrowseQuery,
  useDataRoomFolderPillsQuery,
  Account,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FolderIcon } from "@heroicons/react/24/solid";
import { FileIcon } from "../../components/FileIcon";
import { DataRoomHeader } from "../../components/data_room/DataRoomHeader";
import DeleteFolderModal from "./DeleteFolderModal";
import { SelectFilesActions } from "@/src/components/data_room/data_room_actions/data_room_actions";
import {
  FilterButton,
  FilterOption,
} from "@/src/components/data_room/filters/filter_button";
import { Avatar } from "@/src/components/account/Avatar";
import { getUnixTime, startOfDay, sub } from "date-fns";
import { FolderView } from "./folder_view";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "@/src/store";
import { dataRoomSelectors } from "@/src/store/data-room/selector";
import { TreeView } from "./tree_view";
import { Toggle } from "@/src/components/Toggle";
import { FolderTreeIcon } from "lucide-react";
import { actions, DataRoomView } from "@/src/store/data-room/slice";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BROWSE_QUERY_OPTIONS = {
  staleTime: 60_000,
  refetchOnWindowFocus: false as const,
};

export type FolderPill = {
  ddql?: { id: string; name: string } | null;
  question?: { id: string; title: string } | null;
};

type DataRoomContextType = {
  indexingEnabled: boolean;
  selectedFolders: string[];
  selectedFiles: string[];
  setSelectedFolders: (folders: string[]) => void;
  setSelectedFiles: (files: string[]) => void;
  filters: DataRoomFilters;
  setFilters: (filters: DataRoomFilters) => void;
  folderPills: Map<string, FolderPill>;
};

export const DataRoomContext = createContext<DataRoomContextType>({
  indexingEnabled: false,
  selectedFolders: [],
  selectedFiles: [],
  setSelectedFolders: () => { },
  setSelectedFiles: () => { },
  filters: {},
  setFilters: () => { },
  folderPills: new Map(),
});

export function DataRoom() {
  const client = useGqlClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const { folderId: routeFolderId } = useParams<{ folderId: string }>();

  const dataRoomQuery = useDataRoomFastQuery(
    client,
    {
      dealId: activeDealId ? activeDealId : "",
    },
    {
      ...BROWSE_QUERY_OPTIONS,
      enabled: !!activeDealId,
    },
  );

  const [filters, setFilters] = useState<DataRoomFilters>({});
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [triggerMoveItems, setTriggerMoveItems] = useState<boolean>(false);
  const [triggerDeleteItems, setTriggerDeleteItems] = useState<boolean>(false);

  const folderId =
    routeFolderId || dataRoomQuery.data?.deal.dataRoom.folder.id || "";

  // Only block on Fast when we need it to resolve the root folder id
  if (!routeFolderId && (dataRoomQuery.isPending || !dataRoomQuery.data)) {
    return <Loading />;
  }

  if (!routeFolderId && dataRoomQuery.error) {
    return <div>Something went wrong</div>;
  }

  if (!folderId) {
    return <Loading />;
  }

  return (
    <DataRoomContext.Provider
      value={{
        indexingEnabled: dataRoomQuery.data?.deal.indexing ?? false,
        selectedFolders,
        selectedFiles,
        setSelectedFolders,
        setSelectedFiles,
        filters,
        setFilters,
        folderPills: new Map(),
      }}
    >
      <div className="flex-1 select-none">
        <DataRoomHeader
          folderId={folderId}
          permissions={dataRoomQuery.data?.deal.dataRoom.permissions ?? []}
          recentSearchesKey={dataRoomQuery.data?.deal.id ?? activeDealId}
          dataRoomId={dataRoomQuery.data?.deal.dataRoom.id ?? ""}
          root={`deal/${activeDealId}/documents`}
          title="Documents"
          selectedFolders={selectedFolders}
          selectedFiles={selectedFiles}
          setSelectedFolders={setSelectedFolders}
          setSelectedFiles={setSelectedFiles}
          setTriggerMoveItems={setTriggerMoveItems}
          setTriggerDeleteItems={setTriggerDeleteItems}
        />
        <div className="">
          <DataRoomContent
            folderId={folderId}
            dataRoomId={dataRoomQuery.data?.deal.dataRoom.id ?? ""}
            permissions={dataRoomQuery.data?.deal.dataRoom.permissions ?? []}
            selectedFolders={selectedFolders}
            selectedFiles={selectedFiles}
            setSelectedFolders={setSelectedFolders}
            setSelectedFiles={setSelectedFiles}
            triggerMoveItems={triggerMoveItems}
            setTriggerMoveItems={setTriggerMoveItems}
            triggerDeleteItems={triggerDeleteItems}
            setTriggerDeleteItems={setTriggerDeleteItems}
            indexingEnabled={dataRoomQuery.data?.deal.indexing ?? false}
          />
        </div>
      </div>
    </DataRoomContext.Provider>
  );
}

interface DataRoomContentProps {
  dataRoomId: string;
  folderId: string;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  selectedFolders: string[];
  selectedFiles: string[];
  setSelectedFolders: (folders: string[]) => void;
  setSelectedFiles: (files: string[]) => void;
  triggerMoveItems: boolean;
  setTriggerMoveItems: (trigger: boolean) => void;
  triggerDeleteItems: boolean;
  setTriggerDeleteItems: (trigger: boolean) => void;
  indexingEnabled: boolean;
}

const typeFilterOptions: { value: FileType | "Folder"; label: string }[] = [
  {
    value: "Folder",
    label: "Folder",
  },
  {
    value: FileType.Xlsx,
    label: "Excel",
  },
  {
    value: FileType.Docx,
    label: "Word",
  },
  {
    value: FileType.Pdf,
    label: "PDF",
  },

  {
    value: FileType.Pptx,
    label: "Powerpoint",
  },
];

const lastUpdatedFilterOptions: { value: number; label: string }[] = [
  {
    label: "Today",
    value: getUnixTime(startOfDay(new Date())),
  },
  {
    label: "Last 3 days",
    value: getUnixTime(sub(new Date(), { days: 3 })),
  },
  {
    label: "Last 7 days",
    value: getUnixTime(sub(new Date(), { days: 7 })),
  },
  {
    label: "Last 30 days",
    value: getUnixTime(sub(new Date(), { days: 30 })),
  },
];

function DataRoomContent(props: DataRoomContentProps) {
  const client = useGqlClient();
  const history = useHistory();
  const query = useQuery();
  const location = useLocation();
  const dispatch = useDispatch();
  const folderQuery = useDataRoomFolderBrowseQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
      ...BROWSE_QUERY_OPTIONS,
    },
  );

  const pillsQuery = useDataRoomFolderPillsQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "" && !!folderQuery.data,
      ...BROWSE_QUERY_OPTIONS,
    },
  );

  const folderPills = useMemo(() => {
    const map = new Map<string, FolderPill>();
    for (const folder of pillsQuery.data?.dataRoomFolder.folders ?? []) {
      map.set(folder.id, {
        ddql: folder.ddql,
        question: folder.question,
      });
    }
    return map;
  }, [pillsQuery.data]);

  const dataRoomView = useSelector((state: AppState) =>
    dataRoomSelectors.dataRoomView(state, props.dataRoomId),
  );

  const [createdByOptions, setCreatedByOptions] = useState<
    FilterOption<Pick<Account, "id" | "name" | "avatar">>[]
  >([]);

  const { filters, setFilters } = useContext(DataRoomContext);

  const currentFolderPermission = useMemo(() => {
    const permission = folderQuery.data?.dataRoomFolder.permissions[0];
    if (!permission) return null;
    return {
      id: permission.id,
      name: permission.name,
      description: permission.description,
    };
  }, [
    folderQuery.data?.dataRoomFolder.permissions[0]?.id,
    folderQuery.data?.dataRoomFolder.permissions[0]?.name,
    folderQuery.data?.dataRoomFolder.permissions[0]?.description,
  ]);

  useEffect(() => {
    if (!folderQuery.data) {
      return;
    }

    const filesFoldersCreatedBys = [
      ...folderQuery.data.dataRoomFolder.files.map((file) => file.createdBy),
      ...folderQuery.data.dataRoomFolder.folders.map(
        (folder) => folder.createdBy,
      ),
    ];

    const uniqueCreatedBys = filesFoldersCreatedBys
      .filter(
        (createdBy, index, self) =>
          index === self.findIndex((t) => t.id === createdBy.id),
      )
      .map((createdBy) => ({
        value: createdBy,
        label: createdBy.name,
      }));

    setCreatedByOptions(uniqueCreatedBys);
  }, [folderQuery.data]);

  return (
    <DataRoomContext.Provider
      value={{
        indexingEnabled: props.indexingEnabled,
        selectedFolders: props.selectedFolders,
        selectedFiles: props.selectedFiles,
        setSelectedFolders: props.setSelectedFolders,
        setSelectedFiles: props.setSelectedFiles,
        filters,
        setFilters,
        folderPills,
      }}
    >
      <div className="flex items-center justify-between mb-2 px-2">
        {/* py-0.5 is to keep this the same height as the action buttons so there is no jump */}
        <div className="flex items-center gap-x-2 py-0.5">
          <FilterButton
            defaultLabel="Type"
            options={typeFilterOptions}
            selected={typeFilterOptions.find(
              (option) => option.value === filters.type,
            )}
            onChange={(value) => {
              setFilters({
                ...filters,
                type: value as FileType | "Folder",
              });
            }}
            renderOption={(option) => {
              let icon = null;
              if (option.value === "Folder") {
                icon = <FolderIcon className="h-5 w-5 text-blue-700/70" />;
              } else {
                icon = <FileIcon fileType={option.value as FileType} />;
              }

              return (
                <div className="flex items-center gap-x-2">
                  {icon}
                  <p className="text-sm font-semibold text-gray-500">
                    {option.label}
                  </p>
                </div>
              );
            }}
          />
          <FilterButton
            defaultLabel="Created by"
            options={createdByOptions}
            selected={createdByOptions.find(
              (option) => option.value === filters.createdBy,
            )}
            onChange={(value) => {
              setFilters({
                ...filters,
                createdBy: value as Pick<Account, "id" | "name" | "avatar">,
              });
            }}
            renderOption={(option) => {
              return (
                <div className="flex items-center gap-x-2">
                  <Avatar account={option.value} size="xs" />
                  <p className="text-sm font-semibold text-gray-500">
                    {option.label}
                  </p>
                </div>
              );
            }}
          />
          <FilterButton
            defaultLabel="Last updated"
            options={lastUpdatedFilterOptions}
            selected={lastUpdatedFilterOptions.find(
              (option) => option.value === filters.lastUpdated,
            )}
            onChange={(value) => {
              setFilters({
                ...filters,
                lastUpdated: value as number,
              });
            }}
            renderOption={(option) => {
              return (
                <div className="flex items-center gap-x-2">
                  <p className="text-sm font-semibold text-gray-500">
                    {option.label}
                  </p>
                </div>
              );
            }}
          />

          <Toggle
            options={[
              { id: "folder" as DataRoomView, icon: FolderIcon },
              { id: "tree" as DataRoomView, icon: FolderTreeIcon },
            ]}
            onChange={(o) => {
              dispatch(
                actions.setView({
                  dataRoomId: props.dataRoomId,
                  view: o as DataRoomView,
                }),
              );
            }}
            value={dataRoomView}
            variant="secondary"
            size="sm"
          />
        </div>
        <SelectFilesActions
          permissions={props.permissions}
          folderId={props.folderId}
          selectedFolders={props.selectedFolders}
          selectedFiles={props.selectedFiles}
          setSelectedFolders={props.setSelectedFolders}
          setSelectedFiles={props.setSelectedFiles}
        />
      </div>
      <div className={`overflow-y-scroll ${dataRoomView === "folder" ? "max-h-[calc(100vh-220px)]" : "max-h-[calc(100vh-175px)]"}`}>
        {dataRoomView === "folder" ? (
          <FolderView
            isLoading={folderQuery.isPending}
            error={folderQuery.error}
            folder={
              folderQuery.data ? folderQuery.data.dataRoomFolder : undefined
            }
            selectedFolders={props.selectedFolders}
            selectedFiles={props.selectedFiles}
            setSelectedFolders={props.setSelectedFolders}
            setSelectedFiles={props.setSelectedFiles}
            triggerMoveItems={props.triggerMoveItems}
            setTriggerMoveItems={props.setTriggerMoveItems}
            triggerDeleteItems={props.triggerDeleteItems}
            setTriggerDeleteItems={props.setTriggerDeleteItems}
          />
        ) : (
          <TreeView
            folderId={props.folderId}
            currentFolderName={
              folderQuery.data?.dataRoomFolder.name || "Home"
            }
            hasWriteAccess={Boolean(
              folderQuery.data?.dataRoomFolder.hasFolderWriteAccess,
            )}
            currentFolderPermission={currentFolderPermission}
          />
        )}
      </div>

      <DeleteFolderModal
        open={
          query.get("deleteFolder") !== null && query.get("deleteFolder") !== ""
        }
        onClose={() => {
          history.replace({ pathname: location.pathname, search: "" });
        }}
      />
    </DataRoomContext.Provider>
  );
}

export type DataRoomFilters = {
  type?: FileType | "Folder";
  createdBy?: Pick<Account, "id" | "name" | "avatar">;
  lastUpdated?: number;
};
